import React, { useEffect, useRef, useState } from "react";
import api from "../context/api";
import "../styles/PostResult.css";

const getTodayInputDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateForApi = (inputDate) => {
  const [year, month, day] = String(inputDate || "").split("-");
  if (!year || !month || !day) {
    return "";
  }
  return `${Number(month)}/${Number(day)}/${year}`;
};

const formatDateForDisplay = (inputDate) => {
  const parsed = new Date(`${inputDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return inputDate;
  }
  return parsed.toLocaleDateString();
};

const normalizeDateToMdy = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const mdyMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdyMatch) {
    return `${Number(mdyMatch[1])}/${Number(mdyMatch[2])}/${mdyMatch[3]}`;
  }

  const isoMatch = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    return `${Number(isoMatch[2])}/${Number(isoMatch[3])}/${isoMatch[1]}`;
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return `${parsed.getMonth() + 1}/${parsed.getDate()}/${parsed.getFullYear()}`;
  }

  return raw;
};

const normalizeDayName = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");

const toSortedNumberArray = (values) =>
  (values || [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);

const sameNumberSet = (left, right) => {
  const a = toSortedNumberArray(left);
  const b = toSortedNumberArray(right);

  if (a.length !== b.length) {
    return false;
  }

  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) {
      return false;
    }
  }

  return true;
};

const buildSubmissionKey = (payload) => {
  const dayPart = normalizeDayName(payload.selectedDay).replace(/\s+/g, "-");
  const datePart = normalizeDateToMdy(payload.selectedDate).replace(/\//g, "-");
  const numbersPart = toSortedNumberArray(payload.numbers).join("-");
  const machinePart = toSortedNumberArray(payload.machineNumbers).join("-");

  return `draw-${dayPart}-${datePart}-${numbersPart}-${machinePart}`;
};

export const PostResultScreen = () => {
  const [numbers, setNumbers] = useState(Array.from({ length: 5 }, () => ""));
  const [machineNumbers, setMachineNumbers] = useState(Array.from({ length: 5 }, () => ""));
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedDate, setSelectedDate] = useState(getTodayInputDate());
  const [postedGame, setPostedGame] = useState(null);
  const [isFetchingPostedGame, setIsFetchingPostedGame] = useState(false);
  const [isDeletingPostedGame, setIsDeletingPostedGame] = useState(false);
  const [isRetryingOutcome, setIsRetryingOutcome] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [outcomeJob, setOutcomeJob] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");

  const numberInputRefs = useRef([]);
  const machineNumberInputRefs = useRef([]);
  const announcedJobStatusRef = useRef({});

  const isOutcomeProcessingActive =
    outcomeJob?.status === "queued" || outcomeJob?.status === "running";

  const compactGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 6,
    marginBottom: 20,
  };

  const compactInputStyle = {
    width: "100%",
    minWidth: 0,
    height: 42,
    padding: "0.35rem 0",
    fontSize: "1rem",
    textAlign: "center",
    boxSizing: "border-box",
  };

  const dayOptions = [
    "SUNDAY ASEDA",
    "MONDAY SPECIAL",
    "LUCKY TUESDAY",
    "MIDWEEK",
    "FORTUNE THURSDAY",
    "FRIDAY BONANZA",
    "NATIONAL WEEKLY",
    "NLA VAG MONDAY",
    "NLA NOON RUSH MONDAY",
    "NLA VAG TUESDAY",
    "NLA NOON RUSH TUESDAY",
    "NLA VAG WEDNESDAY",
    "NLA NOON RUSH WEDNESDAY",
    "NLA VAG THURSDAY",
    "NLA NOON RUSH THURSDAY",
    "NLA VAG FRIDAY",
    "NLA NOON RUSH FRIDAY",
    "NLA VAG SATURDAY",
    "NLA NOON RUSH SATURDAY",
    "HUNTEX THURSDAY",
    "HUNTEX FRIDAY",
    "HUNTEX SATURDAY",
    "HUNTEX SUNDAY",
    "HUNTEX MONDAY",
    "HUNTEX AKOMA TUESDAY",
    "HUNTEX WEDNESDAY",
  ];

  const showMessage = (text, type = "info") => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
      setMessageType("info");
    }, 4000);
  };

  const clearInputs = () => {
    setNumbers(Array.from({ length: 5 }, () => ""));
    setMachineNumbers(Array.from({ length: 5 }, () => ""));
    numberInputRefs.current.forEach((el) => el && (el.value = ""));
    machineNumberInputRefs.current.forEach((el) => el && (el.value = ""));
  };

  const handleNumberChange = (index, value, setArray, array, refs) => {
    let numericValue = value.replace(/\D/g, "").slice(0, 2);
    if (numericValue.length > 0) {
      const n = parseInt(numericValue, 10);
      if (!Number.isNaN(n)) {
        if (n === 0) numericValue = "1";
        else if (n > 90) numericValue = "90";
        else numericValue = String(n);
      }
    }

    const updated = [...array];
    updated[index] = numericValue;
    setArray(updated);

    if (numericValue.length === 2 && index < 4) {
      refs.current[index + 1]?.focus();
    }
  };

  useEffect(() => {
    if (!outcomeJob?.jobId || !isOutcomeProcessingActive) {
      return undefined;
    }

    let cancelled = false;

    const pollOutcomeJob = async () => {
      try {
        const response = await api.get(`/outcome-processing-status/${outcomeJob.jobId}`, {
          timeout: 30000,
        });
        if (cancelled) {
          return;
        }

        const nextJob = response?.data?.job;
        if (!nextJob) {
          return;
        }

        setOutcomeJob(nextJob);

        if (
          nextJob.status === "completed" &&
          announcedJobStatusRef.current[nextJob.jobId] !== "completed"
        ) {
          announcedJobStatusRef.current[nextJob.jobId] = "completed";
          showMessage(
            `Outcome processing completed. ${nextJob.totalProcessed || 0} user(s) processed.`,
            "success"
          );
        }

        if (
          nextJob.status === "failed" &&
          announcedJobStatusRef.current[nextJob.jobId] !== "failed"
        ) {
          announcedJobStatusRef.current[nextJob.jobId] = "failed";
          showMessage(nextJob.error || "Outcome processing failed", "error");
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (error?.response?.status === 404) {
          showMessage("Outcome progress expired. Please retry processing.", "error");
          setOutcomeJob((current) =>
            current ? { ...current, status: "failed", error: "Job not found or expired" } : current
          );
        }
      }
    };

    pollOutcomeJob();
    const intervalId = setInterval(pollOutcomeJob, 2000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [isOutcomeProcessingActive, outcomeJob?.jobId]);

  const updateNumbers = async () => {
    if (!selectedDay) {
      showMessage("Please select a day", "error");
      return;
    }

    const formattedSelectedDate = formatDateForApi(selectedDate);
    if (!formattedSelectedDate) {
      showMessage("Please select a valid date", "error");
      return;
    }

    const enteredNla = numbers.filter((n) => String(n || "").trim() !== "");
    const hasAnyNla = enteredNla.length > 0;
    const hasAllNla = enteredNla.length === 5;

    if (hasAnyNla && !hasAllNla) {
      showMessage("Fill all 5 NLA numbers, or leave NLA empty and post machine numbers later.", "error");
      return;
    }

    const parsedNlaNumbers = hasAllNla ? numbers.map((n) => parseInt(n, 10)) : [];
    const validNla = parsedNlaNumbers.every(
      (value) => Number.isFinite(value) && value >= 1 && value <= 90
    );

    if (hasAllNla && !validNla) {
      showMessage("NLA numbers must be between 1 and 90", "error");
      return;
    }

    const machine = machineNumbers.filter((n) => String(n || "").trim() !== "");
    const parsedMachineNumbers = machine.map((n) => parseInt(n, 10));
    const validMachine = parsedMachineNumbers.every(
      (value) => Number.isFinite(value) && value >= 1 && value <= 90
    );

    if (!hasAllNla && parsedMachineNumbers.length === 0) {
      showMessage("Enter NLA numbers first, or provide machine numbers to update an already posted game.", "error");
      return;
    }

    if (!validMachine) {
      showMessage("Machine numbers must be between 1 and 90", "error");
      return;
    }

    const isMachineOnlyUpdate = !hasAllNla;

    const payload = {
      selectedDate: formattedSelectedDate,
      selectedDay,
      machineNumbers: parsedMachineNumbers,
      ...(hasAllNla ? { numbers: parsedNlaNumbers } : {}),
    };

    const requestConfig = {
      timeout: Number(process.env.REACT_APP_UPDATE_RESULTS_TIMEOUT_MS || 240000),
    };

    if (hasAllNla) {
      const idempotencyKey = buildSubmissionKey({
        ...payload,
        numbers: parsedNlaNumbers,
      });

      requestConfig.headers = {
        "x-idempotency-key": idempotencyKey,
      };
    }

    setIsSubmitting(true);
    try {
      const response = await api.post("/updateNumbers", payload, requestConfig);

      const outcomeJobId = response?.data?.outcomeJobId;
      if (outcomeJobId) {
        setOutcomeJob({
          jobId: outcomeJobId,
          status: "queued",
          contextLabel: "newly posted draw",
          totalMatchingUsers: 0,
          totalProcessed: 0,
          percent: 0,
          processedBatches: 0,
        });
      }

      if (outcomeJobId) {
        showMessage("Results submitted successfully. Outcome recalculation started.", "success");
      } else if (isMachineOnlyUpdate) {
        showMessage("Machine numbers posted successfully.", "success");
      } else {
        showMessage("Results updated successfully.", "success");
      }
      setPostedGame(null);
      clearInputs();
    } catch (error) {
      const isTimeoutLike =
        error?.code === "ECONNABORTED" ||
        String(error?.message || "").toLowerCase().includes("timeout") ||
        !error?.response;

      if (isTimeoutLike && hasAllNla) {
        try {
          const verifyResponse = await api.get("/getNumbers/0", { timeout: 30000 });
          const postedNumbers = verifyResponse?.data?.numbers || [];

          const matchedPostedResult = postedNumbers.find((result) => {
            const sameDay =
              normalizeDayName(result?.selectedDay) === normalizeDayName(selectedDay);
            const sameDate =
              normalizeDateToMdy(result?.selectedDate) ===
              normalizeDateToMdy(formattedSelectedDate);
            const sameDraw = sameNumberSet(result?.numbers, payload.numbers);

            return sameDay && sameDate && sameDraw;
          });

          if (matchedPostedResult) {
            showMessage(
              "Results were saved successfully. Server response was delayed.",
              "success"
            );
            clearInputs();
            return;
          }
        } catch (verifyError) {
          // Verification failed; fall through to generic error message.
        }
      }

      showMessage(error.response?.data?.message || "Failed to submit results", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFetchPostedGame = async () => {
    if (!selectedDay) {
      showMessage("Please select a day first", "error");
      return;
    }

    const formattedSelectedDate = formatDateForApi(selectedDate);
    if (!formattedSelectedDate) {
      showMessage("Please select a valid date", "error");
      return;
    }

    setIsFetchingPostedGame(true);
    try {
      const response = await api.get("/posted-game", {
        params: {
          selectedDay,
          selectedDate: formattedSelectedDate,
        },
      });

      const foundGame = response?.data?.result || null;
      setPostedGame(foundGame);

      if (foundGame) {
        const foundNumbers = (foundGame.numbers || []).map((n) => String(n));
        const foundMachineNumbers = (foundGame.machineNumbers || []).map((n) => String(n));

        setNumbers(Array.from({ length: 5 }, (_, i) => foundNumbers[i] || ""));
        setMachineNumbers(Array.from({ length: 5 }, (_, i) => foundMachineNumbers[i] || ""));

        showMessage("Posted game fetched. You can delete or replace it.", "success");
      }
    } catch (error) {
      setPostedGame(null);
      showMessage(error.response?.data?.message || "Failed to fetch posted game", "error");
    } finally {
      setIsFetchingPostedGame(false);
    }
  };

  const handleDeletePostedGame = async () => {
    if (!postedGame?._id) {
      showMessage("No posted game selected to delete", "error");
      return;
    }

    const confirmed = window.confirm(
      "Delete this posted game? You can post a new one immediately after."
    );

    if (!confirmed) {
      return;
    }

    setIsDeletingPostedGame(true);
    try {
      await api.delete(`/posted-game/${postedGame._id}`);
      setPostedGame(null);
      clearInputs();
      showMessage("Posted game deleted. You can now post a new game.", "success");
    } catch (error) {
      showMessage(error.response?.data?.message || "Failed to delete posted game", "error");
    } finally {
      setIsDeletingPostedGame(false);
    }
  };

  const handleRetryOutcomeProcessing = async () => {
    if (!selectedDay) {
      showMessage("Please select a day first", "error");
      return;
    }

    const formattedSelectedDate = formatDateForApi(selectedDate);
    if (!formattedSelectedDate) {
      showMessage("Please select a valid date", "error");
      return;
    }

    setIsRetryingOutcome(true);
    try {
      const response = await api.post("/retryOutcomeProcessing", {
        selectedDay,
        selectedDate: formattedSelectedDate,
      });

      const outcomeJobId = response?.data?.outcomeJobId;
      if (outcomeJobId) {
        setOutcomeJob({
          jobId: outcomeJobId,
          status: "queued",
          contextLabel: `manual retry ${selectedDay} ${formattedSelectedDate}`,
          totalMatchingUsers: 0,
          totalProcessed: 0,
          percent: 0,
          processedBatches: 0,
        });
      }

      showMessage("Outcome reprocessing started.", "success");
    } catch (error) {
      showMessage(
        error.response?.data?.message || "Failed to retry outcome processing",
        "error"
      );
    } finally {
      setIsRetryingOutcome(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: "20px auto", padding: 16 }}>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <img
          src="/images/Huntex.png"
          alt="Hunt Logo"
          style={{ width: 80, height: 80, borderRadius: 40 }}
        />
      </div>

      {message && (
        <div className={`message-alert ${messageType}`} style={{ marginBottom: 12 }}>
          {message}
        </div>
      )}

      <button className="primary-btn" style={{ width: "100%", marginBottom: 12 }}>
        SELECT GAME DATE
      </button>
      <input
        type="date"
        className="form-input"
        style={{ width: "100%", marginBottom: 12 }}
        value={selectedDate}
        onChange={(e) => setSelectedDate(e.target.value)}
      />

      <div style={{ textAlign: "center", color: "#e74c3c", fontWeight: 700, marginBottom: 16 }}>
        Selected Date: {formatDateForDisplay(selectedDate)}
      </div>

      <select
        className="form-input"
        style={{ width: "100%", marginBottom: 20 }}
        value={selectedDay}
        onChange={(e) => setSelectedDay(e.target.value)}
      >
        <option value="">Select a day</option>
        {dayOptions.map((day) => (
          <option key={day} value={day}>
            {day}
          </option>
        ))}
      </select>

      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <button
          className="primary-btn"
          style={{ flex: 1 }}
          onClick={handleFetchPostedGame}
          disabled={isFetchingPostedGame || isSubmitting || isDeletingPostedGame || isRetryingOutcome}
        >
          {isFetchingPostedGame ? "Fetching..." : "Fetch Posted Game"}
        </button>
        <button
          className="submit-btn"
          style={{ flex: 1 }}
          onClick={handleDeletePostedGame}
          disabled={!postedGame || isDeletingPostedGame || isSubmitting || isFetchingPostedGame || isRetryingOutcome}
        >
          {isDeletingPostedGame ? "Deleting..." : "Delete Posted Game"}
        </button>
      </div>

      <button
        className="primary-btn"
        style={{ width: "100%", marginBottom: 16 }}
        onClick={handleRetryOutcomeProcessing}
        disabled={
          isRetryingOutcome ||
          isSubmitting ||
          isFetchingPostedGame ||
          isDeletingPostedGame ||
          isOutcomeProcessingActive
        }
      >
        {isRetryingOutcome ? "Processing..." : "Retry Outcome Processing"}
      </button>

      {outcomeJob && (
        <div
          style={{
            border: "1px solid #dbeafe",
            borderRadius: 10,
            padding: 12,
            marginBottom: 16,
            background: "#eff6ff",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Outcome Recalculation Progress</div>
          <div style={{ marginBottom: 8 }}>
            Status: {String(outcomeJob.status || "queued").toUpperCase()}
          </div>
          <div
            style={{
              height: 10,
              borderRadius: 999,
              background: "#dbeafe",
              overflow: "hidden",
              marginBottom: 8,
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${Math.max(0, Math.min(100, Number(outcomeJob.percent || 0)))}%`,
                background: outcomeJob.status === "failed" ? "#ef4444" : "#2563eb",
                transition: "width 0.3s ease",
              }}
            />
          </div>
          <div style={{ fontSize: 13 }}>
            {Number(outcomeJob.totalProcessed || 0)} / {Number(outcomeJob.totalMatchingUsers || 0)}
            {" "}users processed ({Number(outcomeJob.percent || 0).toFixed(1)}%)
          </div>
          {outcomeJob.error && (
            <div style={{ color: "#b91c1c", marginTop: 8, fontSize: 13 }}>
              Error: {outcomeJob.error}
            </div>
          )}
        </div>
      )}

      {postedGame && (
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            padding: 12,
            marginBottom: 16,
            background: "#f8fafc",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Fetched Posted Game</div>
          <div style={{ marginBottom: 4 }}>Day: {postedGame.selectedDay || "-"}</div>
          <div style={{ marginBottom: 4 }}>Date: {postedGame.selectedDate || "-"}</div>
          <div style={{ marginBottom: 4 }}>
            Numbers: {(postedGame.numbers || []).join(", ") || "-"}
          </div>
          <div>
            Machine: {(postedGame.machineNumbers || []).join(", ") || "-"}
          </div>
        </div>
      )}

      <h3 style={{ textAlign: "center", marginBottom: 12 }}>NLA NUMBERS</h3>
      <div style={compactGridStyle}>
        {numbers.map((value, index) => (
          <input
            key={`nla-${index}`}
            ref={(el) => (numberInputRefs.current[index] = el)}
            type="text"
            maxLength={2}
            inputMode="numeric"
            className="number-input"
            style={compactInputStyle}
            value={value}
            onChange={(e) =>
              handleNumberChange(index, e.target.value, setNumbers, numbers, numberInputRefs)
            }
          />
        ))}
      </div>

      <h3 style={{ textAlign: "center", marginBottom: 12 }}>MACHINE NUMBERS</h3>
      <div style={compactGridStyle}>
        {machineNumbers.map((value, index) => (
          <input
            key={`machine-${index}`}
            ref={(el) => (machineNumberInputRefs.current[index] = el)}
            type="text"
            maxLength={2}
            className="number-input"
            style={compactInputStyle}
            value={value}
            onChange={(e) =>
              handleNumberChange(
                index,
                e.target.value,
                setMachineNumbers,
                machineNumbers,
                machineNumberInputRefs
              )
            }
          />
        ))}
      </div>

      <button
        className="submit-btn"
        style={{ width: "100%", }}
        onClick={updateNumbers}
        disabled={isSubmitting || isOutcomeProcessingActive}
      >
        {isSubmitting ? "Submitting..." : "Send Results"}
      </button>
    </div>
  );
};

export default PostResultScreen;
